/**
 * TakeTourButton.jsx
 * Direct 1-click header trigger button with animated blinking beacon.
 * Automatically launches the specific, contextual tour for the current page or sub-view.
 * (No multi-page dropdown options — each page only runs its own dedicated tour).
 */

import React, { useMemo } from 'react';
import {
    CloseOutlined,
    CompassOutlined,
} from '@ant-design/icons';
import { useProjectTour } from './ProjectManagementTourContext';

export default function TakeTourButton({
    currentNav = 'overview',
    currentSubTab = 'list',
    tourId = null,
    className = '',
}) {
    const {
        startTour,
        inviteDismissed,
        dismissInvite,
        isOpen,
    } = useProjectTour();

    // Determine the exact contextual tour ID for the current page & sub-tab
    const contextualTourId = useMemo(() => {
        if (tourId) return tourId;
        if (currentNav === 'dashboard') return 'projectsList';
        if (currentNav === 'milestones') return 'milestones';
        if (currentNav === 'files') return 'projectFiles';
        if (currentNav === 'inbox') return 'inbox';
        if (currentNav === 'mywork') return 'myWork';
        if (currentNav === 'taskDrawer') return 'taskDrawer';
        if (currentNav === 'createTask') return 'createTask';

        if (currentNav === 'overview') {
            if (currentSubTab === 'kanban') return 'kanban';
            if (currentSubTab === 'timeline') return 'timeline';
            if (currentSubTab === 'sprint') return 'sprint';
            return 'taskList';
        }

        return 'taskList';
    }, [tourId, currentNav, currentSubTab]);

    const handleStartTour = () => {
        startTour(contextualTourId);
    };

    const handleStartFromInvite = () => {
        dismissInvite();
        startTour(contextualTourId);
    };

    return (
        <>
            {/* Direct 1-Click Page Tour Button */}
            <button
                type="button"
                onClick={handleStartTour}
                className={`relative inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${className} bg-white dark:bg-[#1e293b] border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:border-[#03488a] dark:hover:border-[#00aaf1] hover:text-[#03488a] dark:hover:text-[#00aaf1] shadow-xs active:scale-95`}
                title="Start interactive tour for this page"
            >
                {/* Active Blinking Beacon Indicator */}
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00AAF1]"></span>
                </span>

                <CompassOutlined className="text-sm text-[#03488a] dark:text-[#00aaf1]" />
                <span>Tour Guide</span>
            </button>

            {/* First-Time Subtle Floating Invite Toast */}
            {!inviteDismissed && !isOpen && (
                <div className="fixed bottom-6 right-6 z-[1050] max-w-sm w-full bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-800 shadow-2xl rounded-2xl p-4 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <span className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-950/60 text-[#03488a] dark:text-[#a7c8ff] flex items-center justify-center font-bold text-base">
                                ✨
                            </span>
                            <div>
                                <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
                                    New to this view?
                                </h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    Take an interactive tour of this page, its tools, and actions.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={dismissInvite}
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1 cursor-pointer"
                            title="Dismiss"
                        >
                            <CloseOutlined style={{ fontSize: 12 }} />
                        </button>
                    </div>

                    <div className="flex items-center justify-end gap-2.5 mt-3 pt-2 border-t border-slate-100 dark:border-slate-800/80">
                        <button
                            onClick={dismissInvite}
                            className="px-3 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer"
                        >
                            Maybe Later
                        </button>
                        <button
                            onClick={handleStartFromInvite}
                            className="px-3.5 py-1.5 text-xs font-bold text-white rounded-lg transition-all shadow-xs hover:opacity-95 cursor-pointer"
                            style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                        >
                            Start Tour
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
